import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, Search, X, Loader2, ChevronDown, CloudUpload, FileText, Trash2 } from "lucide-react";
import { jwtDecode } from "jwt-decode";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { Dialog, Transition } from "@headlessui/react";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Components & API
import SidebarNav from "../../components/MasterSideBar";
import TopbarNav from "../../components/TopbarNav";
import { fetchProjects } from "../../api/projectApi";
import { getRequirementsByProjectId, bulkUpdateRequirementStatus } from "../../api/requirementApi";
import { createRequisition } from "../../api/procurementApi";
import UploadDocumentModal from "../../components/UploadDocumentModal";

const NON_SELECTABLE_STATUSES = ["Issued", "Rejected", "Cancelled", "Requisitioned"];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const DEPARTMENT_OPTIONS = [
  { value: "operations", label: "Operations" },
  { value: "finance", label: "Finance" },
  { value: "human_resources", label: "Human Resources" },
  { value: "it", label: "IT" },
];

const PROCUREMENT_TYPE_OPTIONS = [
  { value: "goods_services", label: "Goods & Services" },
  { value: "travel", label: "Travel" },
];

const getUserId = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    return decoded._id;
  } catch (error) {
    return null;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// --- Sub-components ---
const FileItem = ({ file, onRemove }) => (
  <div className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white p-2 flex items-center gap-2 hover:shadow-sm transition-all">
    <div className="p-1.5 rounded-lg bg-orange-50 text-orange-600"><FileText size={16} /></div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] font-bold text-gray-800 truncate">{file.originalName}</p>
    </div>
    <button onClick={() => onRemove(file)} className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
      <Trash2 size={14} />
    </button>
  </div>
);

function RequisitionModal({ open, title, onClose, onConfirm, children, confirmLabel = "Confirm", submitting, project, budgetCode }) {
  return (
    <Transition appear show={open} as={React.Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-95 scale-95">
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-3xl bg-white p-8 text-left shadow-2xl transition-all">
                {/* START FIX A: Update Dialog.Title to show Budget Code Banner */}
                <Dialog.Title className="text-xl font-black text-gray-800 uppercase tracking-tight flex justify-between items-center mb-6">
                  <div className="flex flex-col">
                    <span>{title}</span>
                    {/* Display Budget Code if Project is selected */}
                    {project && budgetCode && (
                      <div className="mt-2 bg-blue-50 border-l-4 border-blue-500 p-2 rounded-lg flex justify-between items-center w-full">
                        <span className="text-[10px] font-bold text-blue-800 uppercase tracking-widest">Allocated Budget:</span>
                        <span className="text-xs font-mono text-blue-700 font-bold bg-white px-2 py-0.5 rounded shadow-sm">
                          {budgetCode}
                        </span>
                      </div>
                    )}
                  </div>
                  <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><X size={20} /></button>
                </Dialog.Title>
                <div className="max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">{children}</div>
                <div className="flex justify-end gap-3 mt-8 pt-6 border-t">
                  <button onClick={onClose} className="px-6 py-2.5 text-xs font-black uppercase tracking-widest text-gray-500 hover:text-gray-700">Cancel</button>
                  <button onClick={onConfirm} disabled={submitting} className="bg-orange-600 text-white px-8 py-2.5 rounded-xl font-black uppercase text-xs tracking-widest hover:bg-orange-700 shadow-lg shadow-orange-100 disabled:opacity-50">
                    {submitting ? "Processing..." : confirmLabel}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default function ProgramManagerAccordion() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  const [projects, setProjects] = useState([]);
  const [openProjectId, setOpenProjectId] = useState(null);
  const [requirementsMap, setRequirementsMap] = useState({});
  const [selectedMap, setSelectedMap] = useState({});
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingRequirements, setLoadingRequirements] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPayload, setModalPayload] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const methods = useForm();
  const { register, handleSubmit, control, reset, watch, formState: { errors } } = methods;
  const staffId = getUserId();

  const fetchInitialData = async () => {
    setLoadingProjects(true);
    try {
      const projectsData = await fetchProjects();
      const userId = getUserId();
      const filtered = userId ? projectsData.filter(p => p.assignedProgramManager?._id === userId) : projectsData;
      setProjects(filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch (err) {
      toast.error("Failed to sync projects");
    } finally {
      setLoadingProjects(false);
    }
  };

  useEffect(() => { fetchInitialData(); }, []);

  useEffect(() => {
    if (!openProjectId || requirementsMap[openProjectId]) return;
    const fetchReqs = async () => {
      setLoadingRequirements(prev => ({ ...prev, [openProjectId]: true }));
      try {
        const reqs = await getRequirementsByProjectId(openProjectId);
        setRequirementsMap(prev => ({ ...prev, [openProjectId]: reqs }));
      } catch (err) {
        setRequirementsMap(prev => ({ ...prev, [openProjectId]: [] }));
      } finally {
        setLoadingRequirements(prev => ({ ...prev, [openProjectId]: false }));
      }
    };
    fetchReqs();
  }, [openProjectId, requirementsMap]);

  const toggleSelect = (pid, rid) => {
    const req = (requirementsMap[pid] || []).find(r => (r._id || r.id) === rid);
    if (req && NON_SELECTABLE_STATUSES.includes(req.status)) return;
    setSelectedMap(s => {
      const set = new Set(s[pid] || []);
      set.has(rid) ? set.delete(rid) : set.add(rid);
      return { ...s, [pid]: set };
    });
  };

  const openCreateModal = (projectId) => {
    const sel = selectedMap[projectId];
    const reqs = (requirementsMap[projectId] || []).filter(r => sel.has(r._id || r.id) && r.status === "Pending");
    
    setModalPayload({ projectId, requirements: reqs });
    reset({
      title: "", description: "", dueDate: "",
      priority: PRIORITY_OPTIONS[1],
      departmentsTargeted: [],
      typeOfProcurement: PROCUREMENT_TYPE_OPTIONS[0],
      lineItems: reqs.map(r => ({ requirementId: r._id, itemName: r.name, quantity: r.quantity, unitPrice: r.unitCost }))
    });
    setUploadedDocuments([]);
    setModalOpen(true);
  };

  const onSubmit = async (formData) => {
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        priority: formData.priority?.value,
        typeOfProcurement: formData.typeOfProcurement?.value,
        departmentsTargeted: formData.departmentsTargeted?.map(d => d.value),
        projectId: modalPayload.projectId,
        currentStatus: "draft",
        documentIds: uploadedDocuments.map(doc => doc._id),
        lineItems: modalPayload.requirements.map(r => ({ requirementId: r._id, itemName: r.name, quantity: r.quantity, unitPrice: r.unitCost }))
      };

      await createRequisition(payload);
      const requirementIds = modalPayload.requirements.map(r => r._id || r.id);
      await bulkUpdateRequirementStatus(requirementIds, "Requisitioned", modalPayload.projectId);
      
      toast.success("Requisition Created");
      setModalOpen(false);
      // Refresh
      const reqs = await getRequirementsByProjectId(modalPayload.projectId);
      setRequirementsMap(prev => ({ ...prev, [modalPayload.projectId]: reqs }));
      setSelectedMap(s => ({ ...s, [modalPayload.projectId]: new Set() }));
    } catch (err) {
      toast.error("Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredProjects = useMemo(() => projects.filter(p => 
    p.projectTitle.toLowerCase().includes(searchQuery.toLowerCase()) || 
    p.location?.toLowerCase().includes(searchQuery.toLowerCase())
  ), [projects, searchQuery]);

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
      <ToastContainer position="top-right" theme="colored" />
      <SidebarNav visible={sidebarOpen} onClose={() => setSidebarOpen(false)} collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} onLogout={() => { localStorage.clear(); navigate("/"); }} />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
        <TopbarNav onSidebarToggle={() => setSidebarOpen(true)} onSearchChange={setSearchQuery} />

        <main className="flex-1 p-4 md:p-8 mt-16 lg:mt-20 overflow-y-auto">
          <div className="max-w-[1600px] mx-auto space-y-6">
            
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-black text-gray-800 uppercase tracking-tight">Project Requirements</h1>
                <p className="text-gray-500 text-sm">Review project needs and initiate procurement workflows.</p>
              </div>
            </div>

            <div className="space-y-4">
              {loadingProjects ? (
                <div className="flex justify-center py-20"><Loader2 className="animate-spin text-orange-500" size={40} /></div>
              ) : filteredProjects.map((project) => {
                const pid = project._id || project.id;
                const isOpen = openProjectId === pid;
                const reqs = requirementsMap[pid] || [];
                const selSet = selectedMap[pid] || new Set();

                return (
                  <div key={pid} className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md">
                    <button onClick={() => setOpenProjectId(isOpen ? null : pid)} className="w-full text-left p-6 flex items-center justify-between hover:bg-gray-50 transition-colors">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                           <h3 className="text-lg font-black text-gray-800 uppercase tracking-tight">{project.projectTitle}</h3>
                           <span className={`text-[10px] px-2 py-0.5 rounded-full font-black uppercase ${project.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                             {project.status}
                           </span>
                        </div>
                        <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">{project.location} • {formatDate(project.startDate)} - {formatDate(project.endDate)}</p>
                      </div>
                      <ChevronDown className={`transition-transform duration-300 ${isOpen ? 'rotate-180 text-orange-600' : 'text-gray-400'}`} />
                    </button>

                    {isOpen && (
                      <div className="px-6 pb-6 border-t border-gray-50 bg-gray-50/30">
                        <div className="mt-6 overflow-x-auto rounded-2xl border border-gray-100 bg-white">
                          {loadingRequirements[pid] ? (
                            <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-orange-500" /></div>
                          ) : (
                            <table className="w-full text-left min-w-[900px]">
                              <thead className="bg-gray-50/50">
                                <tr className="text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                                  <th className="px-6 py-4 w-10"></th>
                                  <th className="px-6 py-4">Item Name</th>
                                  <th className="px-6 py-4">Type</th>
                                  <th className="px-6 py-4">Qty</th>
                                  <th className="px-6 py-4">Unit Cost</th>
                                  <th className="px-6 py-4">Total</th>
                                  <th className="px-6 py-4">Status</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-50">
                                {reqs.map((r) => {
                                  const rid = r._id || r.id;
                                  const isDisabled = NON_SELECTABLE_STATUSES.includes(r.status);
                                  return (
                                    <tr key={rid} className={`group ${isDisabled ? 'opacity-40' : 'hover:bg-gray-50'}`}>
                                      <td className="px-6 py-4">
                                        <input type="checkbox" checked={selSet.has(rid)} onChange={() => toggleSelect(pid, rid)} disabled={isDisabled} className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 h-4 w-4" />
                                      </td>
                                      <td className="px-6 py-4 font-bold text-gray-800 text-sm">{r.name}</td>
                                      <td className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">{r.type}</td>
                                      <td className="px-6 py-4 text-xs font-bold text-gray-800">{r.quantity} {r.unit}</td>
                                      <td className="px-6 py-4 text-xs font-bold text-gray-500">${r.unitCost?.toFixed(2)}</td>
                                      <td className="px-6 py-4 text-xs font-black text-orange-600">${r.totalCost?.toFixed(2)}</td>
                                      <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded-full text-[9px] font-black uppercase ${r.status === 'Pending' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                                          {r.status}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                           <button onClick={() => setSelectedMap(s => ({ ...s, [pid]: new Set() }))} className="px-6 py-2 text-xs font-black uppercase tracking-widest text-gray-400 hover:text-gray-600">Clear</button>
                           <button onClick={() => openCreateModal(pid)} disabled={selSet.size === 0} className="bg-orange-600 text-white px-8 py-3 rounded-xl font-black uppercase text-xs tracking-widest hover:bg-orange-700 shadow-lg disabled:opacity-50">
                             Create Requisition ({selSet.size})
                           </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </main>
      </div>

      <FormProvider {...methods}>
        <RequisitionModal 
          open={modalOpen} 
          title="Initiate Requisition" 
          onClose={() => setModalOpen(false)} 
          onConfirm={handleSubmit(onSubmit)} 
          submitting={submitting}
        >
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Requisition Title</label>
                <input {...register("title", { required: true })} className="w-full bg-gray-50 border-none rounded-xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-orange-500/20" placeholder="e.g. Q1 Office Supplies" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Due Date</label>
                <input type="date" {...register("dueDate", { required: true })} className="w-full bg-gray-50 border-none rounded-xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-orange-500/20" />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Description / Justification</label>
              <textarea {...register("description", { required: true })} rows="3" className="w-full bg-gray-50 border-none rounded-xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-orange-500/20" placeholder="Explain the need for these items..." />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Priority</label>
                <Controller name="priority" control={control} render={({ field }) => <Select {...field} options={PRIORITY_OPTIONS} className="text-sm font-bold" />} />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Procurement Type</label>
                <Controller name="typeOfProcurement" control={control} render={({ field }) => <Select {...field} options={PROCUREMENT_TYPE_OPTIONS} className="text-sm font-bold" />} />
              </div>
               <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Target Departments</label>
                <Controller name="departmentsTargeted" control={control} render={({ field }) => <Select {...field} isMulti options={DEPARTMENT_OPTIONS} className="text-sm font-bold" />} />
              </div>
            </div>

            <div className="space-y-3">
               <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Attachments</label>
               <button type="button" onClick={() => setIsUploadModalOpen(true)} className="w-full border-2 border-dashed border-gray-200 rounded-2xl p-6 flex flex-col items-center hover:border-orange-400 hover:bg-orange-50 transition-all">
                  <CloudUpload className="text-orange-500 mb-2" />
                  <span className="text-xs font-black uppercase text-gray-400">Upload Supporting Docs</span>
               </button>
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                 {uploadedDocuments.map((file, i) => <FileItem key={i} file={file} onRemove={(f) => setUploadedDocuments(prev => prev.filter(x => x !== f))} />)}
               </div>
            </div>
          </div>
        </RequisitionModal>
      </FormProvider>

      <UploadDocumentModal isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} staffId={staffId} onUploadSuccessWithFiles={(files) => { setUploadedDocuments(files); setIsUploadModalOpen(false); }} category="Requisition Documents" />
    </div>
  );
}